import api from '../api/api';

/**
 * Create a new assignment - matches the mobile implementation
 * @param {Object} assignmentData - Assignment data
 * @returns {Promise} Promise resolving to created assignment
 */
export const createAssignment = async (assignmentData) => {
  try {
    console.log('Creating assignment with data:', assignmentData);
    
    // Send the new range format directly - backend handles both formats
    const formattedData = {
      title: assignmentData.title,
      description: assignmentData.description,
      type: assignmentData.type || 'new-memorization',
      range: assignmentData.range,  // New range format
      students: assignmentData.students || assignmentData.studentIds,
      center: assignmentData.center,
      dueDate: assignmentData.dueDate,
      matchingQuiz: assignmentData.matchingQuiz || false,
      halaqaId: assignmentData.halaqaId
    };
    
    console.log('Sending to backend:', JSON.stringify(formattedData, null, 2));
    const response = await api.post('/assignments', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error creating assignment:', error);
    throw error.response ? error.response.data : new Error('Network error');
  }
};

/**
 * Get assignments created by a teacher, optionally filtered by halaqa and/or student
 * @param {string} centerId - Center ID
 * @param {string} halaqaId - Halaqa ID (optional)
 * @param {string} studentId - Student ID (optional)
 * @returns {Promise} Promise resolving to list of assignments
 */
export const getTeacherAssignments = async (centerId, halaqaId = null, studentId = null) => {
  try {
    const params = {};
    if (halaqaId) {
      params.halaqaId = halaqaId.toString();
    }
    if (studentId) {
      params.studentId = studentId.toString();
    }
    
    const response = await api.get('/assignments/teacher', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching assignments:', error);
    throw error;
  }
};

/**
 * Get assignments for a student
 * @param {string} studentId - Optional student ID. If not provided, gets assignments for the current user
 * @param {string} halaqaId - Optional halaqa ID to filter assignments by class context
 * @returns {Promise} Promise resolving to list of assignments
 */
export const getStudentAssignments = async (studentId = null, halaqaId = null) => {
  try {
    // If studentId is provided, use the by-student endpoint (for teachers viewing a student)
    // Otherwise, use the student endpoint (for students viewing their own assignments)
    const endpoint = studentId ? `/assignments/by-student/${studentId}` : '/assignments/student';
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('includePracticeLogs', 'true');
    
    // If halaqaId is provided, add it to filter by class context
    if (halaqaId) {
      params.append('halaqaId', halaqaId);
      console.log(`Fetching assignments for student ${studentId || 'current user'} in class ${halaqaId}`);
    }
    
    // Include parameters to request practice logs and optional class filtering
    const response = await api.get(`${endpoint}?${params.toString()}`);
    
    // Process the assignments to add practice status 
    let assignments = response.data;
    
    // Ensure assignments are in an array format
    if (!Array.isArray(assignments)) {
      if (assignments && Array.isArray(assignments.data)) {
        assignments = assignments.data;
      } else if (assignments && assignments.assignments && Array.isArray(assignments.assignments)) {
        assignments = assignments.assignments;
      } else {
        console.warn('Unexpected assignments format:', assignments);
        assignments = [];
      }
    }
    
    // Process assignments to add basic validation
    const validAssignments = assignments.filter(a => a).map(assignment => ({
      ...assignment,
      isPracticed: assignment.isPracticed || 
                 (assignment.practiceLogs && assignment.practiceLogs.length > 0),
      hasAssessment: assignment.assessment && 
                   (assignment.assessment.rating || assignment.assessment.comments),
      _id: assignment._id || assignment.id || `temp-${Math.random()}`
    }));
    
    console.log(`Got ${validAssignments.length} student assignments with practice logs`);
    return validAssignments;
  } catch (error) {
    console.error('Error fetching student assignments:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Get single assignment details
 * @param {string} assignmentId - Assignment ID
 * @param {string} centerId - Center ID (optional)
 * @returns {Promise} Promise resolving to assignment details with populated student data
 */
export const getAssignmentById = async (assignmentId, centerId) => {
  try {
    // Request assignment with populated student data and practice logs
    const response = await api.get(`/assignments/${assignmentId}?populate=students&includePracticeLogs=true`);
    
    // Process the response to ensure student data is complete
    const assignment = response.data;
    
    // If we have student IDs but no student objects, fetch student data
    if (assignment && Array.isArray(assignment.students)) {
      // Handle case where students array contains just IDs
      if (assignment.students.length > 0 && typeof assignment.students[0] === 'string') {
        try {
          console.log('Fetching student details for assignment:', assignmentId);
          // Fetch the first student's details if the students array contains only IDs
          const studentId = assignment.students[0];
          const studentResponse = await api.get(`/students/${studentId}`);
          
          if (studentResponse.data) {
            // Replace the ID with the student object
            assignment.students = [studentResponse.data];
          }
        } catch (studentError) {
          console.error('Error fetching student details:', studentError);
        }
      }
      
      // Get first student ID for practice status check
      const studentId = typeof assignment.students[0] === 'object' 
        ? assignment.students[0]._id 
        : assignment.students[0];
      
      // If no practice logs and we have a student ID, try to fetch them directly
      if ((!assignment.practiceLogs || assignment.practiceLogs.length === 0) && studentId) {
        try {
          console.log(`Fetching practice logs for assignment ${assignmentId}, student ${studentId}`);
          const practiceResponse = await api.get(`/practice-logs?assignmentId=${assignmentId}&studentId=${studentId}`);
          const practiceData = practiceResponse.data;
          
          if (practiceData && practiceData.length > 0) {
            console.log(`Found practice log for assignment ${assignmentId} in direct API call`);
            assignment.practiceLogs = practiceData;
            assignment.isPracticed = true;
          } else {
            console.log(`No practice log found for assignment ${assignmentId}`);
            assignment.practiceLogs = [];
            assignment.isPracticed = false;
          }
        } catch (practiceError) {
          console.error(`Error fetching practice logs for assignment ${assignmentId}:`, practiceError);
        }
      } else if (assignment.practiceLogs && assignment.practiceLogs.length > 0) {
        // If we have practice logs, make sure isPracticed flag is set
        assignment.isPracticed = true;
        console.log(`Assignment ${assignmentId} already has practice logs:`, assignment.practiceLogs.length);
      }
    }
    
    return assignment;
  } catch (error) {
    console.error('Error fetching assignment:', error);
    throw error;
  }
};

/**
 * Update assignment
 * @param {string} assignmentId - Assignment ID
 * @param {Object} updateData - Data to update
 * @returns {Promise} Promise resolving to updated assignment
 */
export const updateAssignment = async (assignmentId, updateData) => {
  try {
    const response = await api.put(`/assignments/${assignmentId}`, updateData);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

/**
 * Delete assignment
 * @param {string} assignmentId - Assignment ID
 * @returns {Promise} Promise resolving to deleted assignment
 */
export const deleteAssignment = async (assignmentId) => {
  try {
    const response = await api.delete(`/assignments/${assignmentId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};

/**
 * Submit assessment for an assignment
 * @param {string} assignmentId - Assignment ID
 * @param {Object} assessmentData - Assessment data
 * @param {string} centerId - Center ID (optional)
 * @returns {Promise} Promise resolving to updated assignment
 */
export const submitAssessment = async (assignmentId, assessmentData, centerId) => {
  try {
    const response = await api.post(`/assignments/${assignmentId}/assess`, assessmentData);
    return response.data;
  } catch (error) {
    console.error('Error submitting assessment:', error);
    throw error;
  }
};

/**
 * Get pending assessments for a teacher (today and overdue)
 * @param {Object} options - Query options
 * @param {string} options.halaqaId - Optional halaqa ID to filter by
 * @param {boolean} options.includeOverdue - Whether to include overdue assignments (default: true)
 * @param {number} options.overdueDays - Number of days back to fetch overdue (default: 7)
 * @returns {Promise} Promise resolving to { today: [], overdue: [], counts: { today: 0, overdue: 0 } }
 */
export const getPendingAssessments = async (options = {}) => {
  try {
    const params = {
      halaqaId: options.halaqaId,
      includeOverdue: options.includeOverdue !== undefined ? options.includeOverdue : true,
      overdueDays: options.overdueDays || 7
    };

    const response = await api.get('/assignments/pending-assessment', { params });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error('Network error');
  }
};